import {
  type PropsWithChildren,
  type ReactNode,
  useCallback,
  useState,
} from "react";

import clsx from "clsx";

import videoTooltipIcon from "@assets/video-tooltip.png";
import { GenericTooltip } from "@components/tooltip-container";
import { Modal } from "@shared/modal";

type VideoToolTipProps = {
  videoUrl: string;
  contentDetails: { title: string; content: ReactNode; kbLink?: string };
  className?: string;
  withoutHover?: boolean;
};

export function VideoToolTip(props: PropsWithChildren<VideoToolTipProps>) {
  const [isOpen, setIsOpen] = useState(false);
  const { children, videoUrl, className, contentDetails } = props;
  const onClose = useCallback(() => setIsOpen(false), []);

  const currentVideoUrl = new URL(videoUrl);
  // Don't show related videos
  currentVideoUrl.searchParams.set("rel", "0");
  // Don't show video title
  currentVideoUrl.searchParams.set("showinfo", "0");
  // Don't show progress bar
  currentVideoUrl.searchParams.set("autohide", "1");
  // Auto play
  currentVideoUrl.searchParams.set("autoplay", "1");
  const newVideoUrl = currentVideoUrl.toString();

  return (
    <>
      <Modal
        isOpen={isOpen}
        onClose={onClose}
        titleContent={<div className="font-bold">{contentDetails.title}</div>}
        size="xl"
      >
        <div className="text-lg font-semibold">
          <iframe
            width="100%"
            height="500px"
            src={newVideoUrl}
            title={contentDetails.title}
            allow={
              "accelerometer; clipboard-write; encrypted-media; " +
              "gyroscope; picture-in-picture; web-share"
            }
            allowFullScreen
          />
        </div>
      </Modal>
      <GenericTooltip
        className={className}
        placement="bottom"
        customIcon={
          <img
            src={videoTooltipIcon}
            alt="videoTooltipIcon"
            style={{ height: "1rem" }}
          />
        }
        content={
          <VideoTooltipContent
            onClickThumbnail={() => {
              setIsOpen(true);
            }}
            {...props}
          />
        }
      >
        {children}
      </GenericTooltip>
    </>
  );
}

export function VideoTooltipContent({
  onClickThumbnail,
  contentDetails,
  videoUrl,
  withoutHover,
}: {
  onClickThumbnail: () => void;
} & VideoToolTipProps) {
  return (
    <div className="flex flex-row">
      <div className="relative">
        <i
          className={clsx(
            "fa-solid fa-sharp fa-circle-play absolute-center text-5xl",
            "z-1 p-2  cursor-pointer",
            {
              "text-white hover:text-primary": !withoutHover,
              "text-primary": withoutHover,
            },
          )}
          onClick={onClickThumbnail}
        />
        <img
          alt={contentDetails.title}
          src={getYoutubeThumbnail(videoUrl, "medium") ?? ""}
          className="aspect-video h-32"
        />
      </div>
      <div className="flex flex-col ml-2 relative w-64">
        <div className="ml-2 mb-2 font-bold tracking-wide">
          {contentDetails.title}
        </div>
        <div className="ml-2">{contentDetails.content}</div>
        {contentDetails.kbLink && (
          <a
            rel="noreferrer"
            href={contentDetails.kbLink}
            target="_blank"
            className={clsx(
              "absolute bottom-0 no-underline hover:bg-cosmic-4",
              "hover:text-cosmic rounded-full py-1 px-2 font-medium",
            )}
          >
            VIEW IN KNOWLEDGE BASE
          </a>
        )}
      </div>
    </div>
  );
}

function getYoutubeThumbnail(url: string, quality?: "low" | "medium" | "high") {
  let videoId: string | undefined;
  let thumbnail: string | undefined;
  let result: RegExpMatchArray | null;
  if ((result = url.match(/youtube\.com.*(\?v=|\/embed\/)(.{11})/))) {
    videoId = result.pop();
  } else if ((result = url.match(/youtu.be\/(.{11})/))) {
    videoId = result.pop();
  }

  if (videoId) {
    if (typeof quality === "undefined") {
      quality = "high";
    }

    let qualityKey = "maxresdefault"; // Max quality
    if (quality === "low") {
      qualityKey = "sddefault";
    } else if (quality === "medium") {
      qualityKey = "mqdefault";
    } else if (quality === "high") {
      qualityKey = "hqdefault";
    }

    thumbnail =
      "http://img.youtube.com/vi/" + videoId + "/" + qualityKey + ".jpg";
    return thumbnail;
  }
  return null;
}
